import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { useLanguage } from '../../contexts/LanguageContext';
import type { TranslationKey } from '../../i18n';
import { renderT } from '../../i18n/renderT';
import { langToSlug } from '../../hooks/useLocalePath';

/* ────────────────────────────────────────────────────
   Types
──────────────────────────────────────────────────── */

type NodeId = 'p1n1' | 'p1n2' | 'p1n3' | 'p1n4' | 'p2na' | 'p2nb' | 'p2nc' | 'p2nd' | 'p2ne';

interface FrameData {
    tag: string;
    items?: string[];
    value?: React.ReactNode;
}

interface PanelData {
    phaseTag: string;
    title: string;
    frames: [FrameData, FrameData, FrameData, FrameData];
    mockupCaption: string;
    mockupFilename: string;
    ctaText: string;
}

/* ────────────────────────────────────────────────────
   SVG Icons (stroke-based, matching the prototype)
──────────────────────────────────────────────────── */

const IconUsers = (
    <svg viewBox="0 0 24 24">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

const IconMaturity = (
    <svg viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4l3 3" />
        <path d="M3.05 11a9 9 0 0 1 17.9 0" />
    </svg>
);

const IconGrid = (
    <svg viewBox="0 0 24 24">
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
    </svg>
);

const IconRoadmap = (
    <svg viewBox="0 0 24 24">
        <path d="M3 3h18v4H3z" />
        <path d="M3 10h12v4H3z" />
        <path d="M3 17h8v4H3z" />
    </svg>
);

const IconDatabase = (
    <svg viewBox="0 0 24 24">
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M21 12c0 1.66-4.03 3-9 3S3 13.66 3 12" />
        <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
    </svg>
);

const IconStar = (
    <svg viewBox="0 0 24 24">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
);

const IconBot = (
    <svg viewBox="0 0 24 24">
        <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73A2 2 0 0 1 10 4a2 2 0 0 1 2-2z" />
    </svg>
);

const IconShield = (
    <svg viewBox="0 0 24 24">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
);

const IconChart = (
    <svg viewBox="0 0 24 24">
        <path d="M18 20V10" />
        <path d="M12 20V4" />
        <path d="M6 20v-6" />
    </svg>
);

/* ────────────────────────────────────────────────────
   Node definitions
──────────────────────────────────────────────────── */

/* Node icons by id (static, no text) */
const NODE_ICONS: Record<NodeId, React.ReactNode> = {
    p1n1: IconUsers, p1n2: IconMaturity, p1n3: IconGrid, p1n4: IconRoadmap,
    p2na: IconDatabase, p2nb: IconStar, p2nc: IconBot, p2nd: IconShield, p2ne: IconChart,
};

const NODE_NUMS: Record<NodeId, string> = {
    p1n1: '01', p1n2: '02', p1n3: '03', p1n4: '04',
    p2na: 'A', p2nb: 'B', p2nc: 'C', p2nd: 'D', p2ne: 'E',
};

const PHASE1_IDS: NodeId[] = ['p1n1', 'p1n2', 'p1n3', 'p1n4'];
const PHASE2_IDS: NodeId[] = ['p2na', 'p2nb', 'p2nc', 'p2nd', 'p2ne'];
const ALL_IDS: NodeId[] = [...PHASE1_IDS, ...PHASE2_IDS];

/* Maps MethodologyPipeline nodeIds → Services page step/module IDs for deep linking */
const NODE_TO_SVC_HASH: Record<NodeId, string> = {
    p1n1: 'p1s1', p1n2: 'p1s2', p1n3: 'p1s3', p1n4: 'p1s4',
    p2na: 'p2sa', p2nb: 'p2sb', p2nc: 'p2sc', p2nd: 'p2sd', p2ne: 'p2se',
};

/* ────────────────────────────────────────────────────
   Panel data — step/module key mapping to svc.* translations
──────────────────────────────────────────────────── */

/* Maps nodeId → svc translation prefix for frame items */
const SVC_PREFIX: Record<NodeId, string> = {
    p1n1: 'svc.p1s1', p1n2: 'svc.p1s2', p1n3: 'svc.p1s3', p1n4: 'svc.p1s4',
    p2na: 'svc.p2sa', p2nb: 'svc.p2sb', p2nc: 'svc.p2sc', p2nd: 'svc.p2sd', p2ne: 'svc.p2se',
};

const MOCKUP_FILENAMES: Record<NodeId, string> = {
    p1n1: 'value-pools-map.pdf', p1n2: 'ai-maturity-score.pdf',
    p1n3: 'use-case-matrix.xlsx', p1n4: 'roadmap-18mo.pdf',
    p2na: 'data-platform-architecture.pdf', p2nb: 'model-monitoring-dashboard',
    p2nc: 'agent-orchestration-flow', p2nd: 'ai-operating-model.pdf',
    p2ne: 'roi-tracking-dashboard',
};

/** Build translated panel data for a given node */
function buildPanel(nodeId: NodeId, t: (key: TranslationKey) => string): PanelData {
    const svc = SVC_PREFIX[nodeId];
    const mk = (suffix: string) => t(`${svc}.${suffix}` as TranslationKey);
    const mt = (suffix: string) => t(`meth.${nodeId}.${suffix}` as TranslationKey);

    return {
        phaseTag: mt('phaseTag'),
        title: mt('label'),
        frames: [
            {
                tag: t('svc.frameInputs' as TranslationKey),
                items: [mk('input1'), mk('input2'), mk('input3'), mk('input4')],
            },
            {
                tag: t('svc.frameActivities' as TranslationKey),
                items: [mk('act1'), mk('act2'), mk('act3'), mk('act4')],
            },
            {
                tag: t('svc.frameDeliverables' as TranslationKey),
                items: [mk('del1'), mk('del2'), mk('del3'), mk('del4')],
            },
            {
                tag: t('svc.frameWhatYouGet' as TranslationKey),
                value: (
                    <>
                        <strong>{mt('valueTitle')}</strong>{' '}
                        {mt('valueDesc')}
                    </>
                ),
            },
        ],
        mockupCaption: t('meth.deliverableCaption' as TranslationKey),
        mockupFilename: MOCKUP_FILENAMES[nodeId],
        ctaText: mt('cta'),
    };
}

/* ── Panels are now built dynamically via buildPanel() ── */


/* ────────────────────────────────────────────────────
   Mockup SVGs (per-panel diagrams from prototype)
──────────────────────────────────────────────────── */

const MOCKUP_SVGS: Record<string, React.ReactNode> = {
    'value-pools-map.pdf': (
        <svg viewBox="0 0 280 210" style={{ width: '100%', height: 'auto' }} xmlns="http://www.w3.org/2000/svg">
            <text x="140" y="18" textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize="8" fontFamily="DM Mono" letterSpacing="2">VALUE POOLS MAP</text>
            {/* Row 1 */}
            <rect x="10" y="28" width="82" height="54" rx="3" fill="rgba(255,107,43,0.18)" stroke="rgba(255,107,43,0.5)" strokeWidth="1"/>
            <text x="51" y="49" textAnchor="middle" fill="#FF6B2B" fontSize="8" fontFamily="DM Mono">REVENUE</text>
            <text x="51" y="62" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="7" fontFamily="DM Mono">{'+12\u201318%'}</text>
            <text x="51" y="73" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6.5" fontFamily="DM Mono">GROWTH</text>
            <rect x="99" y="28" width="82" height="54" rx="3" fill="rgba(255,107,43,0.1)" stroke="rgba(255,107,43,0.25)" strokeWidth="1"/>
            <text x="140" y="49" textAnchor="middle" fill="#FF6B2B" fontSize="8" fontFamily="DM Mono">EFFICIENCY</text>
            <text x="140" y="62" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="7" fontFamily="DM Mono">{'\u201320\u201335%'}</text>
            <text x="140" y="73" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6.5" fontFamily="DM Mono">COST</text>
            <rect x="188" y="28" width="82" height="54" rx="3" fill="rgba(0,212,255,0.08)" stroke="rgba(0,212,255,0.2)" strokeWidth="1"/>
            <text x="229" y="49" textAnchor="middle" fill="#00D4FF" fontSize="8" fontFamily="DM Mono">CX</text>
            <text x="229" y="62" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="7" fontFamily="DM Mono">NPS +15</text>
            <text x="229" y="73" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6.5" fontFamily="DM Mono">EXPERIENCE</text>
            {/* Row 2: business units */}
            <text x="10" y="102" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">Business Units mapped to value pools:</text>
            <rect x="10" y="110" width="56" height="22" rx="3" fill="#141414" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <text x="38" y="124" textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="7" fontFamily="DM Mono">Commercial</text>
            <rect x="72" y="110" width="46" height="22" rx="3" fill="#141414" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <text x="95" y="124" textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="7" fontFamily="DM Mono">Ops</text>
            <rect x="124" y="110" width="56" height="22" rx="3" fill="#141414" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <text x="152" y="124" textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="7" fontFamily="DM Mono">Customer</text>
            <rect x="186" y="110" width="50" height="22" rx="3" fill="#141414" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <text x="211" y="124" textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="7" fontFamily="DM Mono">Finance</text>
            <rect x="242" y="110" width="28" height="22" rx="3" fill="#141414" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <text x="256" y="124" textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="7" fontFamily="DM Mono">HR</text>
            {/* Ambition statement box */}
            <rect x="10" y="143" width="260" height="58" rx="4" fill="#0f0f0f" stroke="rgba(255,107,43,0.3)" strokeWidth="1"/>
            <text x="20" y="157" fill="#FF6B2B" fontSize="7" fontFamily="DM Mono" letterSpacing="1">AI AMBITION STATEMENT</text>
            <text x="20" y="170" fill="rgba(255,255,255,0.55)" fontSize="7.5" fontFamily="Urbanist">{'"Become AI-native in commercial and ops by 2027,'}</text>
            <text x="20" y="182" fill="rgba(255,255,255,0.55)" fontSize="7.5" fontFamily="Urbanist">{'targeting $4.2M incremental impact in year one."'}</text>
            <text x="20" y="194" fill="rgba(255,255,255,0.2)" fontSize="6.5" fontFamily="DM Mono">{'Approved: CEO \u00B7 CFO \u00B7 CTO'}</text>
        </svg>
    ),

    'ai-maturity-score.pdf': (
        <svg viewBox="0 0 280 220" style={{ width: '100%', height: 'auto' }} xmlns="http://www.w3.org/2000/svg">
            <text x="140" y="16" textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize="8" fontFamily="DM Mono" letterSpacing="2">{`AI MATURITY SCORE\u2122`}</text>
            {/* Radar background rings */}
            <polygon points="140,30 210,78 185,160 95,160 70,78" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
            <polygon points="140,50 190,86 170,148 110,148 90,86" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
            <polygon points="140,70 170,94 155,136 125,136 110,94" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
            <polygon points="140,90 150,102 140,124 140,124 130,102" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
            {/* Axes */}
            <line x1="140" y1="30" x2="140" y2="164" stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
            <line x1="140" y1="95" x2="210" y2="78" stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
            <line x1="140" y1="95" x2="185" y2="160" stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
            <line x1="140" y1="95" x2="95" y2="160" stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
            <line x1="140" y1="95" x2="70" y2="78" stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
            {/* Data polygon */}
            <polygon points="140,57 181,86 163,145 117,145 99,86" fill="rgba(255,107,43,0.14)" stroke="#FF6B2B" strokeWidth="1.5"/>
            <circle cx="140" cy="57" r="3.5" fill="#FF6B2B"/>
            <circle cx="181" cy="86" r="3.5" fill="#FF6B2B"/>
            <circle cx="163" cy="145" r="3.5" fill="#FF6B2B"/>
            <circle cx="117" cy="145" r="3.5" fill="#FF6B2B"/>
            <circle cx="99" cy="86" r="3.5" fill="#FF6B2B"/>
            {/* Axis labels */}
            <text x="140" y="24" textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize="7" fontFamily="DM Mono">Data Quality</text>
            <text x="215" y="80" textAnchor="start" fill="rgba(255,255,255,0.35)" fontSize="7" fontFamily="DM Mono">MLOps</text>
            <text x="188" y="168" textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize="7" fontFamily="DM Mono">Governance</text>
            <text x="92" y="168" textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize="7" fontFamily="DM Mono">Architecture</text>
            <text x="65" y="80" textAnchor="end" fill="rgba(255,255,255,0.35)" fontSize="7" fontFamily="DM Mono">Talent</text>
            {/* Center score */}
            <text x="140" y="100" textAnchor="middle" fill="#FF6B2B" fontSize="18" fontWeight="600" fontFamily="Urbanist">2.4</text>
            <text x="140" y="110" textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize="6.5" fontFamily="DM Mono">out of 5.0</text>
            {/* Score bar */}
            <rect x="20" y="178" width="240" height="5" rx="2.5" fill="rgba(255,255,255,0.06)"/>
            <rect x="20" y="178" width="115" height="5" rx="2.5" fill="rgba(255,107,43,0.7)"/>
            <text x="20" y="194" fill="rgba(255,255,255,0.2)" fontSize="6.5" fontFamily="DM Mono">BEGINNER</text>
            <text x="140" y="194" textAnchor="middle" fill="#FF6B2B" fontSize="6.5" fontFamily="DM Mono">{'\u25B2 YOU ARE HERE'}</text>
            <text x="260" y="194" textAnchor="end" fill="rgba(255,255,255,0.2)" fontSize="6.5" fontFamily="DM Mono">AI-NATIVE</text>
        </svg>
    ),

    'use-case-matrix.xlsx': (
        <svg viewBox="0 0 280 215" style={{ width: '100%', height: 'auto' }} xmlns="http://www.w3.org/2000/svg">
            <text x="140" y="14" textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize="8" fontFamily="DM Mono" letterSpacing="2">{`IMPACT \u00D7 FEASIBILITY MATRIX`}</text>
            {/* Grid area */}
            <rect x="30" y="22" width="230" height="168" rx="2" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="1"/>
            {/* Quadrant dividers */}
            <line x1="145" y1="22" x2="145" y2="190" stroke="rgba(255,255,255,0.06)" strokeWidth="1" strokeDasharray="4,3"/>
            <line x1="30" y1="106" x2="260" y2="106" stroke="rgba(255,255,255,0.06)" strokeWidth="1" strokeDasharray="4,3"/>
            {/* Quadrant labels */}
            <text x="87" y="35" textAnchor="middle" fill="rgba(0,255,133,0.35)" fontSize="7" fontFamily="DM Mono">QUICK WINS</text>
            <text x="202" y="35" textAnchor="middle" fill="rgba(255,107,43,0.45)" fontSize="7" fontFamily="DM Mono">STRATEGIC BETS</text>
            <text x="87" y="183" textAnchor="middle" fill="rgba(255,255,255,0.15)" fontSize="7" fontFamily="DM Mono">LOW PRIORITY</text>
            <text x="202" y="183" textAnchor="middle" fill="rgba(255,255,255,0.15)" fontSize="7" fontFamily="DM Mono">MONITOR</text>
            {/* Use case bubbles -- Quick Wins (top-left) */}
            <circle cx="75" cy="58" r="9" fill="rgba(0,255,133,0.25)" stroke="rgba(0,255,133,0.5)" strokeWidth="1.2"/>
            <text x="75" y="61.5" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="6.5" fontFamily="DM Mono">UC-03</text>
            <circle cx="110" cy="75" r="7" fill="rgba(0,255,133,0.18)" stroke="rgba(0,255,133,0.35)" strokeWidth="1"/>
            <text x="110" y="78" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="6" fontFamily="DM Mono">UC-07</text>
            <circle cx="58" cy="88" r="5" fill="rgba(0,255,133,0.12)" stroke="rgba(0,255,133,0.25)" strokeWidth="1"/>
            <text x="58" y="91" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="5.5" fontFamily="DM Mono">UC-11</text>
            {/* Strategic Bets (top-right) */}
            <circle cx="195" cy="50" r="13" fill="rgba(255,107,43,0.2)" stroke="#FF6B2B" strokeWidth="1.5"/>
            <text x="195" y="53.5" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="6.5" fontFamily="DM Mono">UC-01</text>
            <circle cx="230" cy="72" r="10" fill="rgba(255,107,43,0.14)" stroke="rgba(255,107,43,0.4)" strokeWidth="1"/>
            <text x="230" y="75.5" textAnchor="middle" fill="rgba(255,255,255,0.65)" fontSize="6" fontFamily="DM Mono">UC-04</text>
            <circle cx="165" cy="80" r="8" fill="rgba(255,107,43,0.1)" stroke="rgba(255,107,43,0.3)" strokeWidth="1"/>
            <text x="165" y="83.5" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="6" fontFamily="DM Mono">UC-09</text>
            {/* Low priority + monitor (bottom) */}
            <circle cx="65" cy="135" r="5" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <circle cx="100" cy="155" r="4" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
            <circle cx="175" cy="130" r="6" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <circle cx="220" cy="150" r="5" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
            {/* Axis labels */}
            <text x="15" y="106" textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize="7" fontFamily="DM Mono" transform="rotate(-90,15,106)">IMPACT</text>
            <text x="145" y="205" textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize="7" fontFamily="DM Mono">FEASIBILITY</text>
        </svg>
    ),

    'roadmap-18mo.pdf': (
        <svg viewBox="0 0 280 215" style={{ width: '100%', height: 'auto' }} xmlns="http://www.w3.org/2000/svg">
            <text x="140" y="14" textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize="8" fontFamily="DM Mono" letterSpacing="2">18-MONTH ROADMAP</text>
            {/* Quarter labels */}
            <text x="46" y="28" textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">Q1</text>
            <text x="102" y="28" textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">Q2</text>
            <text x="158" y="28" textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">Q3</text>
            <text x="214" y="28" textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">Q4</text>
            <text x="258" y="28" textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">{`Q5\u2013Q6`}</text>
            {/* Track 1 -- Data Foundation */}
            <text x="10" y="48" fill="rgba(255,255,255,0.3)" fontSize="7" fontFamily="DM Mono">Data Foundation</text>
            <rect x="20" y="53" width="90" height="12" rx="2" fill="rgba(255,107,43,0.6)"/>
            <text x="65" y="62" textAnchor="middle" fill="white" fontSize="6.5" fontFamily="DM Mono">Lakehouse MVP</text>
            <rect x="112" y="53" width="60" height="12" rx="2" fill="rgba(255,107,43,0.35)"/>
            <text x="142" y="62" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="6.5" fontFamily="DM Mono">Pipelines</text>
            {/* Track 2 -- Use Case A */}
            <text x="10" y="82" fill="rgba(255,255,255,0.3)" fontSize="7" fontFamily="DM Mono">UC Priority A</text>
            <rect x="65" y="87" width="75" height="12" rx="2" fill="rgba(0,212,255,0.4)"/>
            <text x="102" y="96" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="6.5" fontFamily="DM Mono">Build + Deploy</text>
            <rect x="142" y="87" width="50" height="12" rx="2" fill="rgba(0,212,255,0.2)"/>
            <text x="167" y="96" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="6.5" fontFamily="DM Mono">Scale</text>
            {/* Track 3 -- Agent Layer */}
            <text x="10" y="116" fill="rgba(255,255,255,0.3)" fontSize="7" fontFamily="DM Mono">Agent Layer</text>
            <rect x="112" y="121" width="80" height="12" rx="2" fill="rgba(0,255,133,0.3)"/>
            <text x="152" y="130" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="6.5" fontFamily="DM Mono">Design + Deploy</text>
            <rect x="195" y="121" width="75" height="12" rx="2" fill="rgba(0,255,133,0.18)"/>
            <text x="232" y="130" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="6.5" fontFamily="DM Mono">Expand</text>
            {/* Track 4 -- Governance */}
            <text x="10" y="150" fill="rgba(255,255,255,0.3)" fontSize="7" fontFamily="DM Mono">Governance</text>
            <rect x="20" y="155" width="50" height="12" rx="2" fill="rgba(255,255,255,0.08)"/>
            <text x="45" y="164" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="6.5" fontFamily="DM Mono">Framework</text>
            <rect x="72" y="155" width="198" height="12" rx="2" fill="rgba(255,255,255,0.05)"/>
            <text x="171" y="164" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6.5" fontFamily="DM Mono">{`Ongoing \u00B7 Continuous Improvement`}</text>
            {/* ROI milestones */}
            <line x1="20" y1="178" x2="270" y2="178" stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>
            <text x="10" y="192" fill="rgba(255,107,43,0.5)" fontSize="6.5" fontFamily="DM Mono">VALUE</text>
            <circle cx="110" cy="184" r="4" fill="rgba(255,107,43,0.5)" stroke="#FF6B2B" strokeWidth="1"/>
            <text x="110" y="200" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6" fontFamily="DM Mono">$0.8M</text>
            <circle cx="180" cy="184" r="5" fill="rgba(255,107,43,0.6)" stroke="#FF6B2B" strokeWidth="1"/>
            <text x="180" y="200" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6" fontFamily="DM Mono">$2.1M</text>
            <circle cx="250" cy="184" r="6" fill="rgba(255,107,43,0.75)" stroke="#FF6B2B" strokeWidth="1.5"/>
            <text x="250" y="200" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6" fontFamily="DM Mono">$4.2M</text>
        </svg>
    ),

    'data-platform-architecture.pdf': (
        <svg viewBox="0 0 280 215" style={{ width: '100%', height: 'auto' }} xmlns="http://www.w3.org/2000/svg">
            <text x="140" y="14" textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize="8" fontFamily="DM Mono" letterSpacing="2">LAKEHOUSE ARCHITECTURE</text>
            {/* Layer 1: Sources */}
            <text x="20" y="34" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">SOURCES</text>
            <rect x="20" y="38" width="40" height="18" rx="2" fill="#141414" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <text x="40" y="50" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="6.5" fontFamily="DM Mono">CRM</text>
            <rect x="65" y="38" width="40" height="18" rx="2" fill="#141414" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <text x="85" y="50" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="6.5" fontFamily="DM Mono">ERP</text>
            <rect x="110" y="38" width="50" height="18" rx="2" fill="#141414" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <text x="135" y="50" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="6.5" fontFamily="DM Mono">Streaming</text>
            <rect x="165" y="38" width="40" height="18" rx="2" fill="#141414" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <text x="185" y="50" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="6.5" fontFamily="DM Mono">APIs</text>
            <rect x="210" y="38" width="50" height="18" rx="2" fill="#141414" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <text x="235" y="50" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="6.5" fontFamily="DM Mono">Unstructured</text>
            {/* Arrow down */}
            <line x1="140" y1="58" x2="140" y2="70" stroke="rgba(255,107,43,0.4)" strokeWidth="1.5" strokeDasharray="3,2"/>
            <polygon points="135,68 140,75 145,68" fill="rgba(255,107,43,0.5)"/>
            {/* Layer 2: Ingestion */}
            <rect x="20" y="77" width="240" height="20" rx="3" fill="rgba(255,107,43,0.1)" stroke="rgba(255,107,43,0.25)" strokeWidth="1"/>
            <text x="140" y="90" textAnchor="middle" fill="rgba(255,107,43,0.8)" fontSize="7" fontFamily="DM Mono">{`INGESTION LAYER \u2014 Batch + Streaming + CDC`}</text>
            {/* Arrow down */}
            <line x1="140" y1="99" x2="140" y2="108" stroke="rgba(255,107,43,0.4)" strokeWidth="1.5" strokeDasharray="3,2"/>
            <polygon points="135,106 140,113 145,106" fill="rgba(255,107,43,0.5)"/>
            {/* Layer 3: Storage */}
            <rect x="20" y="114" width="240" height="24" rx="3" fill="rgba(0,212,255,0.08)" stroke="rgba(0,212,255,0.2)" strokeWidth="1"/>
            <text x="140" y="128" textAnchor="middle" fill="rgba(0,212,255,0.7)" fontSize="7.5" fontFamily="DM Mono">{`LAKEHOUSE \u2014 Bronze \u00B7 Silver \u00B7 Gold`}</text>
            {/* Arrow down */}
            <line x1="140" y1="140" x2="140" y2="149" stroke="rgba(0,212,255,0.3)" strokeWidth="1.5" strokeDasharray="3,2"/>
            <polygon points="135,147 140,154 145,147" fill="rgba(0,212,255,0.4)"/>
            {/* Layer 4: Serving */}
            <rect x="20" y="155" width="110" height="20" rx="3" fill="rgba(0,255,133,0.07)" stroke="rgba(0,255,133,0.2)" strokeWidth="1"/>
            <text x="75" y="168" textAnchor="middle" fill="rgba(0,255,133,0.6)" fontSize="7" fontFamily="DM Mono">ML Feature Store</text>
            <rect x="150" y="155" width="110" height="20" rx="3" fill="rgba(0,255,133,0.07)" stroke="rgba(0,255,133,0.2)" strokeWidth="1"/>
            <text x="205" y="168" textAnchor="middle" fill="rgba(0,255,133,0.6)" fontSize="7" fontFamily="DM Mono">{'Analytics & BI'}</text>
            {/* Governance bar */}
            <rect x="260" y="38" width="14" height="137" rx="3" fill="rgba(255,107,43,0.06)" stroke="rgba(255,107,43,0.15)" strokeWidth="1"/>
            <text x="267" y="107" textAnchor="middle" fill="rgba(255,107,43,0.4)" fontSize="6.5" fontFamily="DM Mono" transform="rotate(90,267,107)">{`GOVERNANCE \u00B7 OBSERVABILITY \u00B7 DATA QUALITY`}</text>
            {/* Status badges */}
            <rect x="20" y="185" width="55" height="14" rx="2" fill="rgba(0,255,133,0.12)" stroke="rgba(0,255,133,0.3)" strokeWidth="1"/>
            <text x="47" y="195" textAnchor="middle" fill="rgba(0,255,133,0.7)" fontSize="6.5" fontFamily="DM Mono">{'\u2713 Prod'}</text>
            <rect x="80" y="185" width="60" height="14" rx="2" fill="rgba(0,255,133,0.12)" stroke="rgba(0,255,133,0.3)" strokeWidth="1"/>
            <text x="110" y="195" textAnchor="middle" fill="rgba(0,255,133,0.7)" fontSize="6.5" fontFamily="DM Mono">{'\u2713 Monitored'}</text>
            <rect x="145" y="185" width="55" height="14" rx="2" fill="rgba(0,255,133,0.12)" stroke="rgba(0,255,133,0.3)" strokeWidth="1"/>
            <text x="172" y="195" textAnchor="middle" fill="rgba(0,255,133,0.7)" fontSize="6.5" fontFamily="DM Mono">{'\u2713 Governed'}</text>
        </svg>
    ),

    'model-monitoring-dashboard': (
        <svg viewBox="0 0 280 215" style={{ width: '100%', height: 'auto' }} xmlns="http://www.w3.org/2000/svg">
            <text x="140" y="14" textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize="8" fontFamily="DM Mono" letterSpacing="2">MODEL PERFORMANCE</text>
            {/* KPI row */}
            <rect x="10" y="22" width="60" height="40" rx="3" fill="#111" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
            <text x="40" y="36" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6.5" fontFamily="DM Mono">ACCURACY</text>
            <text x="40" y="52" textAnchor="middle" fill="#00FF85" fontSize="16" fontWeight="600" fontFamily="Urbanist">94%</text>
            <rect x="76" y="22" width="60" height="40" rx="3" fill="#111" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
            <text x="106" y="36" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6.5" fontFamily="DM Mono">PRECISION</text>
            <text x="106" y="52" textAnchor="middle" fill="#00FF85" fontSize="16" fontWeight="600" fontFamily="Urbanist">91%</text>
            <rect x="142" y="22" width="60" height="40" rx="3" fill="#111" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
            <text x="172" y="36" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6.5" fontFamily="DM Mono">LATENCY</text>
            <text x="172" y="52" textAnchor="middle" fill="#00D4FF" fontSize="16" fontWeight="600" fontFamily="Urbanist">38ms</text>
            <rect x="208" y="22" width="62" height="40" rx="3" fill="#111" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
            <text x="239" y="36" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6.5" fontFamily="DM Mono">DRIFT</text>
            <text x="239" y="52" textAnchor="middle" fill="#FF6B2B" fontSize="16" fontWeight="600" fontFamily="Urbanist">OK</text>
            {/* Performance chart */}
            <text x="10" y="78" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">Accuracy over time (last 30d)</text>
            <rect x="10" y="83" width="260" height="72" rx="3" fill="#0d0d0d" stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>
            {/* Grid lines */}
            <line x1="10" y1="107" x2="270" y2="107" stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
            <line x1="10" y1="131" x2="270" y2="131" stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
            {/* Chart line */}
            <polyline points="20,148 50,138 80,120 110,110 130,118 155,105 175,99 200,97 220,94 245,92 265,91" fill="none" stroke="#00FF85" strokeWidth="1.5"/>
            <polyline points="20,148 50,138 80,120 110,110 130,118 155,105 175,99 200,97 220,94 245,92 265,91 265,155 20,155" fill="rgba(0,255,133,0.05)"/>
            {/* Y labels */}
            <text x="8" y="107" textAnchor="end" fill="rgba(255,255,255,0.2)" fontSize="6" fontFamily="DM Mono">90%</text>
            <text x="8" y="131" textAnchor="end" fill="rgba(255,255,255,0.2)" fontSize="6" fontFamily="DM Mono">80%</text>
            {/* Business impact */}
            <text x="10" y="172" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">Business impact (vs. baseline)</text>
            <rect x="10" y="178" width="78" height="28" rx="3" fill="rgba(0,255,133,0.07)" stroke="rgba(0,255,133,0.2)" strokeWidth="1"/>
            <text x="49" y="189" textAnchor="middle" fill="rgba(0,255,133,0.6)" fontSize="6.5" fontFamily="DM Mono">Revenue uplift</text>
            <text x="49" y="200" textAnchor="middle" fill="#00FF85" fontSize="11" fontWeight="600" fontFamily="Urbanist">+$340K</text>
            <rect x="95" y="178" width="78" height="28" rx="3" fill="rgba(0,212,255,0.07)" stroke="rgba(0,212,255,0.2)" strokeWidth="1"/>
            <text x="134" y="189" textAnchor="middle" fill="rgba(0,212,255,0.6)" fontSize="6.5" fontFamily="DM Mono">Cost reduction</text>
            <text x="134" y="200" textAnchor="middle" fill="#00D4FF" fontSize="11" fontWeight="600" fontFamily="Urbanist">{'\u2013$180K'}</text>
            <rect x="180" y="178" width="90" height="28" rx="3" fill="rgba(255,107,43,0.07)" stroke="rgba(255,107,43,0.2)" strokeWidth="1"/>
            <text x="225" y="189" textAnchor="middle" fill="rgba(255,107,43,0.7)" fontSize="6.5" fontFamily="DM Mono">Productivity gain</text>
            <text x="225" y="200" textAnchor="middle" fill="#FF6B2B" fontSize="11" fontWeight="600" fontFamily="Urbanist">+22%</text>
        </svg>
    ),

    'agent-orchestration-flow': (
        <svg viewBox="0 0 280 215" style={{ width: '100%', height: 'auto' }} xmlns="http://www.w3.org/2000/svg">
            <text x="140" y="14" textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize="8" fontFamily="DM Mono" letterSpacing="2">AGENT ORCHESTRATION</text>
            {/* Orchestrator */}
            <rect x="95" y="22" width="90" height="28" rx="4" fill="rgba(0,212,255,0.1)" stroke="#00D4FF" strokeWidth="1.5"/>
            <text x="140" y="34" textAnchor="middle" fill="#00D4FF" fontSize="7.5" fontFamily="DM Mono">ORCHESTRATOR</text>
            <text x="140" y="44" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6.5" fontFamily="DM Mono">LangGraph / AutoGen</text>
            {/* Lines from orchestrator to agents */}
            <line x1="97" y1="50" x2="45" y2="80" stroke="rgba(0,212,255,0.25)" strokeWidth="1" strokeDasharray="4,2"/>
            <line x1="117" y1="50" x2="100" y2="80" stroke="rgba(0,212,255,0.25)" strokeWidth="1" strokeDasharray="4,2"/>
            <line x1="163" y1="50" x2="180" y2="80" stroke="rgba(0,212,255,0.25)" strokeWidth="1" strokeDasharray="4,2"/>
            <line x1="183" y1="50" x2="235" y2="80" stroke="rgba(0,212,255,0.25)" strokeWidth="1" strokeDasharray="4,2"/>
            {/* Agent nodes */}
            <rect x="16" y="80" width="58" height="26" rx="3" fill="rgba(255,107,43,0.1)" stroke="rgba(255,107,43,0.35)" strokeWidth="1"/>
            <text x="45" y="91" textAnchor="middle" fill="rgba(255,107,43,0.85)" fontSize="6.5" fontFamily="DM Mono">Sales Agent</text>
            <text x="45" y="101" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6" fontFamily="DM Mono">{`CRM \u00B7 Pipeline`}</text>
            <rect x="80" y="80" width="58" height="26" rx="3" fill="rgba(255,107,43,0.1)" stroke="rgba(255,107,43,0.35)" strokeWidth="1"/>
            <text x="109" y="91" textAnchor="middle" fill="rgba(255,107,43,0.85)" fontSize="6.5" fontFamily="DM Mono">Ops Agent</text>
            <text x="109" y="101" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6" fontFamily="DM Mono">{`ERP \u00B7 Workflow`}</text>
            <rect x="144" y="80" width="64" height="26" rx="3" fill="rgba(255,107,43,0.1)" stroke="rgba(255,107,43,0.35)" strokeWidth="1"/>
            <text x="176" y="91" textAnchor="middle" fill="rgba(255,107,43,0.85)" fontSize="6.5" fontFamily="DM Mono">CS Agent</text>
            <text x="176" y="101" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6" fontFamily="DM Mono">{`Tickets \u00B7 NPS`}</text>
            <rect x="214" y="80" width="56" height="26" rx="3" fill="rgba(255,107,43,0.1)" stroke="rgba(255,107,43,0.35)" strokeWidth="1"/>
            <text x="242" y="91" textAnchor="middle" fill="rgba(255,107,43,0.85)" fontSize="6.5" fontFamily="DM Mono">Finance Agent</text>
            <text x="242" y="101" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6" fontFamily="DM Mono">{'Reports \u00B7 FP&A'}</text>
            {/* Tool/integration layer */}
            <line x1="140" y1="108" x2="140" y2="120" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <rect x="20" y="120" width="240" height="20" rx="3" fill="#111" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
            <text x="140" y="132" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="7" fontFamily="DM Mono">{`TOOL LAYER \u2014 APIs \u00B7 Databases \u00B7 External Services`}</text>
            {/* Guardrails + observability */}
            <rect x="20" y="148" width="115" height="20" rx="3" fill="rgba(255,107,43,0.06)" stroke="rgba(255,107,43,0.15)" strokeWidth="1"/>
            <text x="77" y="161" textAnchor="middle" fill="rgba(255,107,43,0.5)" fontSize="7" fontFamily="DM Mono">{'\u2713 Guardrails Active'}</text>
            <rect x="145" y="148" width="115" height="20" rx="3" fill="rgba(0,255,133,0.06)" stroke="rgba(0,255,133,0.15)" strokeWidth="1"/>
            <text x="202" y="161" textAnchor="middle" fill="rgba(0,255,133,0.5)" fontSize="7" fontFamily="DM Mono">{'\u2713 Full Observability'}</text>
            {/* Stats */}
            <text x="20" y="184" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">Executions today:</text>
            <text x="100" y="184" fill="#00FF85" fontSize="7" fontFamily="DM Mono">1,247</text>
            <text x="140" y="184" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">Avg latency:</text>
            <text x="200" y="184" fill="#00D4FF" fontSize="7" fontFamily="DM Mono">280ms</text>
            <text x="20" y="198" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">Human escalations:</text>
            <text x="120" y="198" fill="#FF6B2B" fontSize="7" fontFamily="DM Mono">3.2%</text>
            <text x="160" y="198" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">Success rate:</text>
            <text x="220" y="198" fill="#00FF85" fontSize="7" fontFamily="DM Mono">96.8%</text>
        </svg>
    ),

    'ai-operating-model.pdf': (
        <svg viewBox="0 0 280 215" style={{ width: '100%', height: 'auto' }} xmlns="http://www.w3.org/2000/svg">
            <text x="140" y="14" textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize="8" fontFamily="DM Mono" letterSpacing="2">AI GOVERNANCE FRAMEWORK</text>
            {/* CoE box at top */}
            <rect x="90" y="22" width="100" height="24" rx="3" fill="rgba(255,107,43,0.12)" stroke="rgba(255,107,43,0.4)" strokeWidth="1.5"/>
            <text x="140" y="32" textAnchor="middle" fill="rgba(255,107,43,0.9)" fontSize="7.5" fontFamily="DM Mono">AI CENTER OF</text>
            <text x="140" y="41" textAnchor="middle" fill="rgba(255,107,43,0.9)" fontSize="7.5" fontFamily="DM Mono">EXCELLENCE</text>
            {/* Lines down */}
            <line x1="100" y1="46" x2="55" y2="72" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <line x1="120" y1="46" x2="120" y2="72" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <line x1="160" y1="46" x2="160" y2="72" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <line x1="180" y1="46" x2="225" y2="72" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            {/* Domain nodes */}
            <rect x="20" y="72" width="68" height="20" rx="3" fill="#111" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <text x="54" y="85" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="6.5" fontFamily="DM Mono">AI Strategy</text>
            <rect x="96" y="72" width="48" height="20" rx="3" fill="#111" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <text x="120" y="85" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="6.5" fontFamily="DM Mono">Engineering</text>
            <rect x="148" y="72" width="24" height="20" rx="3" fill="#111" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <text x="160" y="85" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="6.5" fontFamily="DM Mono">Risk</text>
            <rect x="178" y="72" width="82" height="20" rx="3" fill="#111" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
            <text x="219" y="85" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="6.5" fontFamily="DM Mono">Change Mgmt</text>
            {/* RACI table */}
            <text x="10" y="108" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">RACI by AI Domain</text>
            {/* Table header */}
            <rect x="10" y="113" width="260" height="14" rx="0" fill="#141414"/>
            <text x="80" y="123" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6.5" fontFamily="DM Mono">Use Case Dev</text>
            <text x="155" y="123" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6.5" fontFamily="DM Mono">Model Governance</text>
            <text x="230" y="123" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="6.5" fontFamily="DM Mono">Data Quality</text>
            {/* Rows */}
            <line x1="10" y1="127" x2="270" y2="127" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
            <text x="30" y="137" fill="rgba(255,255,255,0.3)" fontSize="6.5" fontFamily="DM Mono">CPO/CTO</text>
            <text x="80" y="137" textAnchor="middle" fill="#FF6B2B" fontSize="7" fontFamily="DM Mono">A</text>
            <text x="155" y="137" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="7" fontFamily="DM Mono">R</text>
            <text x="230" y="137" textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">C</text>
            <line x1="10" y1="141" x2="270" y2="141" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
            <text x="30" y="151" fill="rgba(255,255,255,0.3)" fontSize="6.5" fontFamily="DM Mono">AI CoE Lead</text>
            <text x="80" y="151" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="7" fontFamily="DM Mono">R</text>
            <text x="155" y="151" textAnchor="middle" fill="#FF6B2B" fontSize="7" fontFamily="DM Mono">A</text>
            <text x="230" y="151" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="7" fontFamily="DM Mono">R</text>
            <line x1="10" y1="155" x2="270" y2="155" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
            <text x="30" y="165" fill="rgba(255,255,255,0.3)" fontSize="6.5" fontFamily="DM Mono">Data Engineer</text>
            <text x="80" y="165" textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">C</text>
            <text x="155" y="165" textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">C</text>
            <text x="230" y="165" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="7" fontFamily="DM Mono">R</text>
            {/* Compliance badges */}
            <rect x="10" y="180" width="58" height="14" rx="2" fill="rgba(0,255,133,0.07)" stroke="rgba(0,255,133,0.2)" strokeWidth="1"/>
            <text x="39" y="190" textAnchor="middle" fill="rgba(0,255,133,0.6)" fontSize="6.5" fontFamily="DM Mono">{`LGPD \u2713`}</text>
            <rect x="74" y="180" width="58" height="14" rx="2" fill="rgba(0,255,133,0.07)" stroke="rgba(0,255,133,0.2)" strokeWidth="1"/>
            <text x="103" y="190" textAnchor="middle" fill="rgba(0,255,133,0.6)" fontSize="6.5" fontFamily="DM Mono">{`ISO 27001 \u2713`}</text>
            <rect x="138" y="180" width="58" height="14" rx="2" fill="rgba(0,255,133,0.07)" stroke="rgba(0,255,133,0.2)" strokeWidth="1"/>
            <text x="167" y="190" textAnchor="middle" fill="rgba(0,255,133,0.6)" fontSize="6.5" fontFamily="DM Mono">{`AI Act \u2713`}</text>
        </svg>
    ),

    'roi-tracking-dashboard': (
        <svg viewBox="0 0 280 215" style={{ width: '100%', height: 'auto' }} xmlns="http://www.w3.org/2000/svg">
            <text x="140" y="14" textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize="8" fontFamily="DM Mono" letterSpacing="2">{'ROI & VALUE DASHBOARD'}</text>
            {/* Top KPIs */}
            <rect x="10" y="22" width="78" height="36" rx="3" fill="#111" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
            <text x="49" y="34" textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize="6.5" fontFamily="DM Mono">TOTAL VALUE (YTD)</text>
            <text x="49" y="50" textAnchor="middle" fill="#00FF85" fontSize="15" fontWeight="700" fontFamily="Urbanist">$3.8M</text>
            <rect x="96" y="22" width="78" height="36" rx="3" fill="#111" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
            <text x="135" y="34" textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize="6.5" fontFamily="DM Mono">REVENUE UPLIFT</text>
            <text x="135" y="50" textAnchor="middle" fill="#FF6B2B" fontSize="15" fontWeight="700" fontFamily="Urbanist">+$2.1M</text>
            <rect x="182" y="22" width="88" height="36" rx="3" fill="#111" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
            <text x="226" y="34" textAnchor="middle" fill="rgba(255,255,255,0.25)" fontSize="6.5" fontFamily="DM Mono">COST REDUCTION</text>
            <text x="226" y="50" textAnchor="middle" fill="#00D4FF" fontSize="15" fontWeight="700" fontFamily="Urbanist">{'\u2013$1.7M'}</text>
            {/* Value accumulation chart */}
            <text x="10" y="72" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">Value captured vs. projected (12mo)</text>
            <rect x="10" y="77" width="260" height="66" rx="3" fill="#0d0d0d" stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>
            {/* Area chart */}
            <polyline points="20,135 50,132 80,125 110,118 135,112 160,102 185,93 210,85 240,80 265,74" fill="none" stroke="rgba(255,107,43,0.3)" strokeWidth="1" strokeDasharray="4,3"/>
            <polyline points="20,140 50,137 80,128 110,119 135,111 160,100 185,89 210,81" fill="none" stroke="#FF6B2B" strokeWidth="2"/>
            <polyline points="20,140 50,137 80,128 110,119 135,111 160,100 185,89 210,81 210,143 20,143" fill="rgba(255,107,43,0.08)"/>
            <text x="215" y="85" fill="rgba(255,107,43,0.5)" fontSize="6" fontFamily="DM Mono">{'\u25B2 Actual'}</text>
            <text x="242" y="76" fill="rgba(255,255,255,0.2)" fontSize="6" fontFamily="DM Mono">{'\u2014 Proj.'}</text>
            {/* Value by use case */}
            <text x="10" y="158" fill="rgba(255,255,255,0.2)" fontSize="7" fontFamily="DM Mono">Value by use case</text>
            <rect x="10" y="163" width="72" height="8" rx="2" fill="rgba(255,107,43,0.7)"/>
            <text x="86" y="170" fill="rgba(255,255,255,0.35)" fontSize="6.5" fontFamily="DM Mono">{`Churn Prediction \u00B7 $1.2M`}</text>
            <rect x="10" y="175" width="50" height="8" rx="2" fill="rgba(0,212,255,0.5)"/>
            <text x="64" y="182" fill="rgba(255,255,255,0.35)" fontSize="6.5" fontFamily="DM Mono">{`Demand Forecast \u00B7 $0.9M`}</text>
            <rect x="10" y="187" width="34" height="8" rx="2" fill="rgba(0,255,133,0.4)"/>
            <text x="48" y="194" fill="rgba(255,255,255,0.35)" fontSize="6.5" fontFamily="DM Mono">{`CS Automation \u00B7 $0.6M`}</text>
            <rect x="150" y="163" width="24" height="8" rx="2" fill="rgba(255,255,255,0.1)"/>
            <text x="178" y="170" fill="rgba(255,255,255,0.35)" fontSize="6.5" fontFamily="DM Mono">{`Pricing AI \u00B7 $0.4M`}</text>
            <rect x="150" y="175" width="18" height="8" rx="2" fill="rgba(255,255,255,0.07)"/>
            <text x="172" y="182" fill="rgba(255,255,255,0.35)" fontSize="6.5" fontFamily="DM Mono">{`+ 3 more \u00B7 $0.7M`}</text>
            <text x="10" y="208" fill="rgba(255,107,43,0.4)" fontSize="6.5" fontFamily="DM Mono">{`Next board report: Apr 2025 \u00B7 On track \u2713`}</text>
        </svg>
    ),
};

/* ────────────────────────────────────────────────────
   Sub-components
──────────────────────────────────────────────────── */

interface DetailPanelProps {
    nodeId: NodeId;
    isOpen: boolean;
    onClose: () => void;
    serviceHref: string;
}

const DetailPanel: React.FC<DetailPanelProps> = ({ nodeId, isOpen, onClose, serviceHref }) => {
    const { t } = useLanguage();
    const panel = useMemo(() => buildPanel(nodeId, t), [nodeId, t]);

    return (
        <div
            className={`meth-detail-wrap${isOpen ? ' open' : ''}`}
            id={`panel-${nodeId}`}
        >
            <div className="dp-header">
                <span className="dp-phase-tag">{panel.phaseTag}</span>
                <div className="dp-title">{panel.title}</div>
                <button className="dp-close" aria-label="Close" onClick={onClose}>
                    &#x2715;
                </button>
            </div>
            <div className="dp-body">
                <div className="dp-frames">
                    {panel.frames.map((frame, i) => (
                        <div className="dp-frame" key={i}>
                            <div className="frame-tag">{frame.tag}</div>
                            {frame.items ? (
                                <ul className="frame-list">
                                    {frame.items.map((item, j) => (
                                        <li key={j}>{item}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="frame-value">{frame.value}</p>
                            )}
                        </div>
                    ))}
                </div>
                <div className="dp-mockup">
                    <div className="mockup-caption">{panel.mockupCaption}</div>
                    <div className="mockup-artifact">
                        <div className="artifact-chrome">
                            <div className="a-dot" />
                            <div className="a-dot" />
                            <div className="a-dot" />
                            <span>{panel.mockupFilename}</span>
                        </div>
                        <div className="artifact-inner">
                            {MOCKUP_SVGS[panel.mockupFilename] ?? (
                                <svg viewBox="0 0 280 210" style={{ width: '100%', height: 'auto' }} xmlns="http://www.w3.org/2000/svg">
                                    <text x="140" y="105" textAnchor="middle" fill="rgba(255,255,255,0.15)" fontSize="10" fontFamily="DM Mono">[Preview]</text>
                                </svg>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="dp-cta-bar">
                <span className="dp-cta-text">{panel.ctaText}</span>
                <Link
                    to={serviceHref}
                    className="btn-primary"
                    style={{ fontSize: 13, padding: '9px 16px' }}
                >
                    {t(nodeId.startsWith('p1') ? 'meth.explorePhase' as TranslationKey : 'meth.exploreModule' as TranslationKey)}
                    <svg viewBox="0 0 16 16" fill="none">
                        <path
                            d="M3 8h10M9 4l4 4-4 4"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </Link>
            </div>
        </div>
    );
};

/* ────────────────────────────────────────────────────
   Main component
──────────────────────────────────────────────────── */

const MethodologyPipeline: React.FC = () => {
    const { language, t } = useLanguage();
    const lp = (path: string) => `/${langToSlug(language)}${path}`;
    const [activeNode, setActiveNode] = useState<NodeId | null>(null);

    useScrollReveal();

    const handleNodeClick = useCallback((id: NodeId) => {
        setActiveNode((prev) => (prev === id ? null : id));
    }, []);

    const handleClosePanel = useCallback(() => {
        setActiveNode(null);
    }, []);

    const renderNode = (id: NodeId) => (
        <button
            key={id}
            className={`meth-node${activeNode === id ? ' active' : ''}`}
            data-node={id}
            onClick={() => handleNodeClick(id)}
            type="button"
        >
            <div className="node-num">{NODE_NUMS[id]}</div>
            <div className="node-icon">{NODE_ICONS[id]}</div>
            <div className="node-lbl">{t(`meth.${id}.label` as TranslationKey)}</div>
        </button>
    );

    return (
        <section className="section" id="methodology">
            <div className="container">
                {/* ── Header ──────────────────────────────── */}
                <div className="meth-system-eyebrow reveal">
                    <div>
                        <div className="section-label">{t('meth.label' as TranslationKey)}</div>
                        <h2 className="section-headline" style={{ marginBottom: 8 }}>
                            {renderT(t('meth.headline' as TranslationKey))}
                        </h2>
                        <p className="section-sub" style={{ marginBottom: 0 }}>
                            {t('meth.sub' as TranslationKey)}
                        </p>
                    </div>
                </div>

                {/* ── Pipeline ────────────────────────────── */}
                <div className="meth-pipeline reveal">
                    {/* Phase 1 */}
                    <div className="meth-phase ph1">
                        <div className="meth-ph-header">
                            <div className="meth-ph-badge">{t('meth.ph1Badge' as TranslationKey)}</div>
                            <div className="meth-ph-label">{t('meth.ph1Label' as TranslationKey)}</div>
                            <div className="meth-ph-dur">{t('meth.ph1Dur' as TranslationKey)}</div>
                        </div>
                        <div className="meth-nodes">
                            {PHASE1_IDS.map(renderNode)}
                        </div>
                    </div>

                    {/* Connector arrow */}
                    <div className="meth-connector">
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#FF6B2B"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M5 12h14M13 6l6 6-6 6" />
                        </svg>
                    </div>

                    {/* Phase 2 */}
                    <div className="meth-phase ph2">
                        <div className="meth-ph-header">
                            <div className="meth-ph-badge">{t('meth.ph2Badge' as TranslationKey)}</div>
                            <div className="meth-ph-label">{t('meth.ph2Label' as TranslationKey)}</div>
                            <div className="meth-ph-dur">{t('meth.ph2Dur' as TranslationKey)}</div>
                        </div>
                        <div className="meth-nodes">
                            {PHASE2_IDS.map(renderNode)}
                        </div>
                    </div>
                </div>

                {/* ── Detail Panels ───────────────────────── */}
                {ALL_IDS.map((id) => (
                    <DetailPanel
                        key={id}
                        nodeId={id}
                        isOpen={activeNode === id}
                        onClose={handleClosePanel}
                        serviceHref={lp(`/services#${NODE_TO_SVC_HASH[id]}`)}
                    />
                ))}

                {/* ── Section CTA ─────────────────────────── */}
                <div style={{ textAlign: 'center', marginTop: 48 }} className="reveal">
                    <Link to={lp('/services')} className="btn-primary">
                        {t('meth.cta' as TranslationKey)}
                        <svg viewBox="0 0 16 16" fill="none">
                            <path
                                d="M3 8h10M9 4l4 4-4 4"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default MethodologyPipeline;
